import React, { FC, useCallback } from 'react';
import { startStripeOnboarding } from 'src/payments';
import { getPaymentEventSource, onError, saveConsent } from 'src/payments/utils/utils';
import injectStore from 'src/util/injectStore';
import { AppStore } from 'src/stores/AppStore';
import { defineMessages } from 'src/i18n';
import {
  TopContainer,
  Illustration,
  ValueProps,
  ButtonContainer
} from '../../PaymentBasicElements';
import {
  OnboardModalMessages,
  OnboardModalProps,
  PaymentEventActivePaymentProvider,
  PaymentEventComponent
} from 'src/payments/types';
import { ConsentType } from '@invoice-simple/common';
import { useTrackPaymentsViewedEvent } from 'src/payments/hooks/useTrackPaymentsViewedEvent';

const messages = defineMessages({
  header: {
    id: 'stripeModal.prompt.title',
    defaultMessage: 'Set Up Stripe'
  },
  description1: {
    id: 'stripeModal.prompt.description1',
    defaultMessage: "Here's what you'll need"
  },
  listItem1Description: {
    id: 'stripeModal.prompt.listItem1Description',
    defaultMessage:
      'Basic details about your business, such as business type, address, and phone number'
  },

  listItem2Description: {
    id: 'stripeModal.prompt.listItem2Description',
    defaultMessage: 'Your Social Security Number and/or Employer Identification Number (EIN)'
  },

  listItem3Description: {
    id: 'stripeModal.prompt.listItem3Description',
    defaultMessage: "Bank account info for where you'd like to receive payments"
  },

  tosPrefix: {
    id: 'stripeModal.prompt.tosPrefix',
    defaultMessage: "By proceeding, you're agreeing to our"
  },
  tos: {
    id: 'stripeModal.prompt.tos',
    defaultMessage: 'Payments Terms of Use'
  },
  goToStripe: { id: 'stripeModal.prompt.goToStripe', defaultMessage: 'Continue To Stripe' }
});

const StripeInitialModal: FC<
  OnboardModalProps & {
    store: AppStore;
  }
> = ({ origin, closeModal, store: { user } }) => {
  const ft = (message: OnboardModalMessages) => message.defaultMessage;
  const stripeSharedMetadata = {
    activePaymentProvider: PaymentEventActivePaymentProvider.STRIPE,
    source: getPaymentEventSource()
  };
  const handleConsent = useCallback(async () => {
    const consentMessage = `${messages.tosPrefix} ${messages.tos}`;
    await saveConsent({
      user,
      consentMessage,
      consentValue: 'v0',
      consentType: ConsentType.STRIPE_ONBOARDING
    });
  }, []);

  const handleClick = useCallback(async () => {
    try {
      user.events.trackAction('payments-cta', {
        ...stripeSharedMetadata,
        component: PaymentEventComponent.modalInitial
      });
      await startStripeOnboarding(origin, true);
      handleConsent();

      closeModal();
    } catch (error) {
      onError({ title: 'Error initiating Stripe onboarding', body: error.message });
    }
  }, []);

  const onClickXIcon = () => {
    user.events.trackAction('payments-cta', {
      ...stripeSharedMetadata,
      component: PaymentEventComponent.modalCloseInitial
    });
    closeModal();
  };

  const onClickTosLink = () => {
    user.events.trackAction('payments-cta', {
      ...stripeSharedMetadata,
      component: PaymentEventComponent.tos
    });
  };

  const paymentsViewedMeta = {
    ...stripeSharedMetadata,
    component: PaymentEventComponent.modalInitial
  };

  useTrackPaymentsViewedEvent(paymentsViewedMeta, user.events);

  return (
    <>
      <div data-testid="stripeInitialModal" />
      <TopContainer
        onClickXIcon={onClickXIcon}
        headerMessage={ft(messages.header)}
        dataTestId="stripe--modal-x-button"
      />
      <main>
        <Illustration url={'/images/kb-illustration.png'} width={826} height={472} />
        <ValueProps>
          <b>{ft(messages.description1)}</b>
          <ul className={'py-0 p-8 marker:font-bold'}>
            <li className={'my-6'}>{ft(messages.listItem1Description)}</li>
            <li className={'my-6'}>{ft(messages.listItem2Description)}</li>
            <li className={'my-6'}>{ft(messages.listItem3Description)}</li>
          </ul>
          <p>
            {ft(messages.tosPrefix)}&nbsp;
            <a
              data-testid="stripe--modal-tos-link"
              onClick={onClickTosLink}
              className={'text-blue-600 font-bold'}
              href={'https://www.invoicesimple.com/payments-terms'}
              target="_blank"
              rel="noopener noreferrer">
              {ft(messages.tos)}
            </a>
          </p>
        </ValueProps>
      </main>
      <ButtonContainer
        dataTestId="stripe--modal-submit-button"
        handleClick={handleClick}
        buttonMessage={ft(messages.goToStripe)}
      />
    </>
  );
};

export default injectStore(StripeInitialModal);
